<template>
    <div ref="suggestions" class="suggestion-list p-2">
        <div v-if="loading" class="suggestion-loading">
            <CompassLoader />
        </div>
        <div v-else-if="showFakeData">
            <div v-for="(n, index) in 4" :key="n" class="fake-user" @click="enterHandler(index)">
                <CompassAvatar
                    size="extra-small"
                    photo="/ressources/logged/img/icon/svg/user.svg"
                />
                <div class="fake-user__text" />
                <CompassFeatureBadge variant="upsell" size="small" />
            </div>
        </div>
        <template v-else-if="items.length > 0">
            <div
                v-for="(staff, index) in items"
                :key="staff.id"
                class="suggestion-list__item p-2 m-0"
                :class="{ 'is-selected': navigatedStaffsIndex === index }"
                @mouseover="navigatedStaffsIndex = index"
                @click="enterHandler(index)"
            >
                <avatar-randomized
                    :avatarImage="staff.avatar"
                    :firstname="staff.firstname"
                    :lastname="staff.lastname"
                    :size="'extra-small'"
                    class="mr-1"
                />
                <span class="name">{{ staff.firstname }} {{ staff.lastname }}</span>
                <span class="email"> - {{ staff.email }}</span>
            </div>
        </template>
        <div v-else class="suggestion-list__item is-empty p-2">
            {{ $l10n.__('Aucune correspondance') }}
        </div>
    </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import AvatarRandomized from 'vcomponents/utilities/AvatarRandomized.vue';

const corpStore = createNamespacedHelpers('corp');
const subscriptionFeaturesStore = createNamespacedHelpers('subscriptionFeatures');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    name: 'MentionSuggestionRichText',
    components: {
        AvatarRandomized,
    },
    props: {
        items: {
            type: Array,
            required: true,
        },
        command: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            showFakeData: false,
            query: null,
            navigatedStaffsIndex: 0,
        };
    },
    computed: {
        ...corpStore.mapState(['pricingVersion']),
        ...subscriptionFeaturesStore.mapGetters({
            subscriptionFeatures: 'getSubscriptionFeatures',
        }),
        getsubscriptionFeaturesCollaborativeSharingSuite() {
            return window.sellsyapp.config.globalProperties.$store.state.subscriptionFeatures
                .collaborative_sharing_suite;
        },
        getPricingVersion() {
            return window.sellsyapp.config.globalProperties.$store.state.corp.pricingVersion;
        },
    },
    watch: {
        items() {
            this.navigatedStaffsIndex = 0;
        },
    },
    mounted() {
        if (
            this.getPricingVersion >= 5 &&
            this.getsubscriptionFeaturesCollaborativeSharingSuite === false
        ) {
            this.showFakeData = true;
        }
    },
    methods: {
        ...popupPanelMapActions(['open']),
        // Used with CustomSuggestion.js
        onKeyDown({ event }) {
            if (event.key === 'ArrowUp') {
                this.upHandler();
                return true;
            }
            if (event.key === 'ArrowDown') {
                this.downHandler();
                return true;
            }
            if (event.key === 'Enter') {
                this.enterHandler();
                return true;
            }
            return false;
        },
        // navigate to the previous item
        // if it's the first item, navigate to the last one
        upHandler() {
            this.navigatedStaffsIndex =
                (this.navigatedStaffsIndex + this.items.length - 1) % this.items.length;
        },
        // navigate to the next item
        // if it's the last item, navigate to the first one
        downHandler() {
            this.navigatedStaffsIndex = (this.navigatedStaffsIndex + 1) % this.items.length;
        },
        enterHandler() {
            const staff = this.items[this.navigatedStaffsIndex];
            if (this.showFakeData) {
                this.showFeatureModal();
                return;
            }
            if (staff) {
                EventBus.emit('onMentionAddedInEditing', true);
                // Action to add the mention in the editor
                this.command({
                    id: staff.id,
                    label: `${staff.firstname} ${staff.lastname}`,
                });
            }
        },
        showFeatureModal() {
            // Open the feature modal
            // $store is undefined in this component because we are in a custom Vue instance (see CustomSuggestion.js)
            // so we use the global window.sellsyapp.config.globalProperties.$store
            window.sellsyapp.config.globalProperties.$store.state.popupPanel.featureModal.props = {
                feature: 'collaborative_sharing_suite',
            };
            window.sellsyapp.config.globalProperties.$store.state.popupPanel.featureModal.display = true;
        },
        // Used with CustomSuggestion.js
        setLoading(value) {
            this.loading = value;
        },
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.mention-suggestion {
    color: rgba(#000, 0.6);
}

.suggestion-list {
    max-height: 304px;
    overflow-y: scroll;

    &__item {
        border-radius: 4px;
        cursor: pointer;
        margin-bottom: 0.2rem;
        padding: 0.2rem 0.5rem;

        &:last-child {
            margin-bottom: 0;
        }

        &.is-selected,
        &.is-selected:hover {
            background-color: $primary-05;
        }

        &.is-empty {
            cursor: default;
        }

        .name {
            color: $gray-100;
        }

        .email {
            color: $grey;
        }
    }

    .suggestion-loading {
        min-height: 40px;
        display: flex;
        justify-content: center;
        align-items: center;
    }
}

.tippy-box[data-theme~='mention'] {
    background-color: $white;
    border-radius: 6px;
    box-shadow: 0 0 12px rgba(17, 17, 17, 0.24);
    color: $gray-100;
    min-width: 440px;
    padding: 0;

    .tippy-content {
        padding: 0;
    }
}
</style>
<style lang="scss" scoped>
.fake-user {
    align-items: center;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    gap: var(--spacing-1);
    padding: var(--spacing-1);

    &:hover {
        background: var(--colors-component-background-actionable-hover);
    }

    &__text {
        background: var(--colors-common-background-primary-hover-alt);
        border-radius: 4px;
        height: 16px;
        width: 326px;
    }
    &:nth-of-type(2) {
        .fake-user__text {
            width: 341px;
        }
    }
    &:nth-of-type(4) {
        .fake-user__text {
            width: 319px;
        }
    }
    &:nth-of-type(5) {
        .fake-user__text {
            width: 333px;
        }
    }
}
</style>
